import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import {
  bulkCsvCourseUpload,
  downloadSampleCsvTemplate,
  downloadCsvErrorReport,
} from '../../../services/bulkCsvCourseUploadService';

const BulkCsvCourseUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [fatalError, setFatalError] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const resetState = () => {
    setFile(null);
    setProgress(null);
    setResult(null);
    setFatalError(null);
  };

  const handleClose = () => {
    if (uploading) return;
    resetState();
    onClose?.();
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile?.name?.split('.')?.pop()?.toLowerCase();
    if (ext !== 'csv') {
      setFatalError('Please upload a CSV file (.csv)');
      return;
    }
    if (selectedFile?.size > 10 * 1024 * 1024) {
      setFatalError('File size must be under 10 MB');
      return;
    }
    setFile(selectedFile);
    setFatalError(null);
    setResult(null);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const dropped = e?.dataTransfer?.files?.[0];
    handleFileSelect(dropped);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    setFatalError(null);
    setProgress({ phase: 'parsing', rowIndex: 0, totalRows: 0 });

    try {
      const uploadResult = await bulkCsvCourseUpload(file, {
        onProgress: (p) => setProgress(p),
      });

      if (uploadResult?.fatalError) {
        setFatalError(uploadResult?.fatalError);
      } else {
        setResult(uploadResult);
        if (uploadResult?.summary?.totalRowsUploaded > 0) {
          onSuccess?.();
        }
      }
    } catch (err) {
      setFatalError(err?.message || 'An unexpected error occurred');
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const progressLabel = () => {
    if (!progress) return '';
    if (progress?.phase === 'parsing') return 'Reading CSV file…';
    if (progress?.phase === 'validating') return 'Validating rows…';
    if (progress?.phase === 'uploading') {
      if (progress?.instituteName) {
        return `Uploading "${progress?.instituteName}, ${progress?.city}" — row ${progress?.rowIndex}/${progress?.totalRows}`;
      }
      return `Uploading row ${progress?.rowIndex}/${progress?.totalRows}`;
    }
    return 'Processing…';
  };

  const progressPercent = () => {
    if (!progress || !progress?.totalRows) return 5;
    if (progress?.phase === 'parsing' || progress?.phase === 'validating') return 10;
    return Math.min(99, Math.round(10 + (progress?.rowIndex / progress?.totalRows) * 89));
  };

  const { summary, errors } = result || {};

  const statusColor = (status) => {
    if (status === 'success') return 'bg-success';
    if (status === 'partial') return 'bg-warning';
    if (status === 'not_found') return 'bg-error';
    if (status === 'skipped') return 'bg-muted-foreground';
    return 'bg-error';
  };

  const statusLabel = (status) => {
    if (status === 'success') return 'Success';
    if (status === 'partial') return 'Partial';
    if (status === 'not_found') return 'Not Found';
    if (status === 'skipped') return 'Skipped';
    return 'Failed';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="FileText" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Bulk CSV Course Upload</h2>
              <p className="text-xs text-muted-foreground">Matches by Institute Name + City — handles multiple campuses</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* How it works */}
          {!result && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={16} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700 space-y-1.5">
                  <p className="font-semibold">How Option 4A works</p>
                  <ul className="text-blue-600 space-y-1 list-disc list-inside">
                    <li>One CSV file — all institutes and courses in a single sheet</li>
                    <li>Each row has <strong>institute_name</strong> + <strong>city</strong> columns to identify the campus</li>
                    <li>Multiple campuses with the same name are distinguished by city</li>
                    <li>Valid rows upload even if other rows fail</li>
                    <li>Download the sample template below to see the exact format</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Required columns info */}
          {!result && (
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <p className="text-xs font-semibold text-foreground mb-2">Required Columns</p>
              <div className="flex flex-wrap gap-1.5">
                {['institute_name', 'city', 'course_name', 'category', 'course_type', 'level', 'duration']?.map((col) => (
                  <span key={col} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-mono">
                    {col}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Optional: specialization, fees, show_fee, eligibility, admission_process, placement_stats, accreditation
              </p>
            </div>
          )}

          {/* Template download */}
          {!result && (
            <div className="flex items-center justify-between bg-success/5 border border-success/30 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <Icon name="Download" size={16} color="var(--color-success)" />
                <div>
                  <p className="text-sm font-medium text-foreground">Download Sample CSV Template</p>
                  <p className="text-xs text-muted-foreground">Includes 5 sample rows with Amrita &amp; Anna University examples</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={downloadSampleCsvTemplate}>
                Download
              </Button>
            </div>
          )}

          {/* File drop zone */}
          {!result && (
            <div
              onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef?.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-primary bg-primary/5'
                  : file
                  ? 'border-success bg-success/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
              } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => handleFileSelect(e?.target?.files?.[0])}
              />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <Icon name="FileCheck2" size={36} color="var(--color-success)" />
                  <p className="font-semibold text-foreground">{file?.name}</p>
                  <p className="text-xs text-muted-foreground">{(file?.size / 1024)?.toFixed(1)} KB · Click to change</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Icon name="Upload" size={36} color="var(--color-muted-foreground)" className="opacity-50" />
                  <p className="font-medium text-foreground">Drop your CSV file here</p>
                  <p className="text-sm text-muted-foreground">or click to browse · .csv · Max 10 MB</p>
                </div>
              )}
            </div>
          )}

          {/* Fatal error */}
          {fatalError && (
            <div className="bg-error/10 border border-error rounded-lg p-4 flex gap-3">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-error text-sm">Upload Failed</p>
                <p className="text-sm text-error/80 mt-0.5">{fatalError}</p>
              </div>
            </div>
          )}

          {/* Progress */}
          {uploading && progress && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{progressLabel()}</span>
                <span className="font-medium text-foreground">{progressPercent()}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent()}%` }}
                />
              </div>
            </div>
          )}

          {/* Results */}
          {result && summary && (
            <div className="space-y-4">

              {/* ── Confirmation Banner ── */}
              {summary?.totalRowsUploaded > 0 && summary?.totalRowsFailed === 0 && summary?.totalRowsSkipped === 0 ? (
                <div className="bg-success/10 border border-success/40 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
                  </div>
                  <div>
                    <p className="font-bold text-success text-base">Upload Complete!</p>
                    <p className="text-sm text-success/80 mt-0.5">
                      All <strong>{summary?.totalRowsUploaded}</strong> course{summary?.totalRowsUploaded !== 1 ? 's' : ''} were uploaded successfully.
                    </p>
                  </div>
                </div>
              ) : summary?.totalRowsUploaded > 0 ? (
                <div className="bg-warning/10 border border-warning/40 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-warning/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
                  </div>
                  <div>
                    <p className="font-bold text-warning text-base">Upload Completed with Issues</p>
                    <p className="text-sm text-warning/80 mt-1 leading-relaxed">
                      <strong>{summary?.totalRowsUploaded}</strong> course{summary?.totalRowsUploaded !== 1 ? 's' : ''} uploaded successfully
                      {summary?.totalRowsFailed > 0 && (
                        <> · <strong>{summary?.totalRowsFailed}</strong> row{summary?.totalRowsFailed !== 1 ? 's' : ''} failed</>
                      )}
                      {summary?.totalRowsSkipped > 0 && (
                        <> · <strong>{summary?.totalRowsSkipped}</strong> row{summary?.totalRowsSkipped !== 1 ? 's' : ''} skipped</>
                      )}
                    </p>
                    {(summary?.totalRowsFailed > 0 || summary?.totalRowsSkipped > 0) && (
                      <p className="text-xs text-warning/70 mt-1">Download the error report below to review and fix the failed rows.</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-error/10 border border-error/40 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-error/20 flex items-center justify-center flex-shrink-0">
                    <Icon name="XCircle" size={20} color="var(--color-error)" />
                  </div>
                  <div>
                    <p className="font-bold text-error text-base">Upload Failed</p>
                    <p className="text-sm text-error/80 mt-0.5">
                      No courses were uploaded.
                      {summary?.totalRowsFailed > 0 && <> <strong>{summary?.totalRowsFailed}</strong> row{summary?.totalRowsFailed !== 1 ? 's' : ''} failed.</>}
                      {summary?.totalRowsSkipped > 0 && <> <strong>{summary?.totalRowsSkipped}</strong> row{summary?.totalRowsSkipped !== 1 ? 's' : ''} skipped.</>}
                    </p>
                    <p className="text-xs text-error/70 mt-1">Check the error details below and fix your CSV before re-uploading.</p>
                  </div>
                </div>
              )}

              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total Rows', value: summary?.totalRows, icon: 'Rows3', color: 'text-primary bg-primary/10' },
                  { label: 'Courses Uploaded', value: summary?.totalRowsUploaded, icon: 'CheckCircle2', color: 'text-success bg-success/10' },
                  { label: 'Rows Failed', value: summary?.totalRowsFailed, icon: 'XCircle', color: 'text-error bg-error/10' },
                  { label: 'Rows Skipped', value: summary?.totalRowsSkipped, icon: 'SkipForward', color: 'text-warning bg-warning/10' },
                ]?.map((card) => (
                  <div key={card?.label} className="bg-card border border-border rounded-lg p-3 text-center">
                    <div className={`w-8 h-8 rounded-lg ${card?.color} flex items-center justify-center mx-auto mb-2`}>
                      <Icon name={card?.icon} size={16} />
                    </div>
                    <p className="text-xl font-bold text-foreground">{card?.value ?? 0}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{card?.label}</p>
                  </div>
                ))}
              </div>

              {/* Per-institute results */}
              {summary?.instituteResults?.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-muted/40 px-4 py-2 border-b border-border">
                    <p className="text-sm font-semibold text-foreground">Institute Results</p>
                  </div>
                  <div className="divide-y divide-border max-h-52 overflow-y-auto">
                    {summary?.instituteResults?.map((ir, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColor(ir?.status)}`} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{ir?.institute_name}</p>
                            <p className="text-xs text-muted-foreground">{ir?.city}</p>
                          </div>
                          {ir?.status === 'not_found' && (
                            <span className="text-xs text-error bg-error/10 px-1.5 py-0.5 rounded flex-shrink-0">Not Found</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 text-xs ml-3">
                          {ir?.status !== 'not_found' && ir?.status !== 'skipped' && (
                            <>
                              <span className="text-success font-medium">✓ {ir?.uploaded}</span>
                              {ir?.failed > 0 && <span className="text-error font-medium">✗ {ir?.failed}</span>}
                            </>
                          )}
                          {(ir?.status === 'not_found' || ir?.status === 'skipped') && (
                            <span className="text-error font-medium">{ir?.total} skipped</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error table */}
              {errors?.length > 0 && (
                <div className="border border-error/30 rounded-lg overflow-hidden">
                  <div className="bg-error/5 px-4 py-2 border-b border-error/20 flex items-center justify-between">
                    <p className="text-sm font-semibold text-error">{errors?.length} Error{errors?.length !== 1 ? 's' : ''} Found</p>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      onClick={() => downloadCsvErrorReport(errors, summary)}
                    >
                      Download Report
                    </Button>
                  </div>
                  <div className="overflow-x-auto max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/30 sticky top-0">
                        <tr>
                          {['Row', 'Column', 'Cell Value', 'Error']?.map((h) => (
                            <th key={h} className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {errors?.map((err, i) => (
                          <tr key={i} className="hover:bg-error/5">
                            <td className="px-3 py-2 text-muted-foreground">{err?.row ?? '—'}</td>
                            <td className="px-3 py-2 text-muted-foreground">{err?.column ?? '—'}</td>
                            <td className="px-3 py-2 text-muted-foreground max-w-[120px] truncate">{String(err?.value ?? '—')}</td>
                            <td className="px-3 py-2 text-error max-w-[220px]">{err?.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Confirmation handled by top banner */}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0 bg-muted/20">
          {result ? (
            <>
              <Button variant="outline" onClick={resetState}>
                Upload Another File
              </Button>
              <Button variant="default" onClick={handleClose}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={uploading}>
                Cancel
              </Button>
              <Button
                variant="default"
                iconName={uploading ? 'Loader2' : 'Upload'}
                iconClassName={uploading ? 'animate-spin' : ''}
                onClick={handleUpload}
                disabled={!file || uploading}
              >
                {uploading ? 'Uploading…' : 'Upload CSV File'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkCsvCourseUploadModal;
